import React, { useEffect, useState } from 'react';
import { Navbar, Container, Nav, NavDropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Briefcase, Calendar, Video, Mail, Shield, User, LogOut } from 'lucide-react';
import logo from '../assets/logo.png';
import Avatar from './Avatar';
import { clearSession } from '../utils/Utils';

const LINKS = [
  { to: '/home', label: 'Inicio', icon: Home },
  { to: '/networking', label: 'Networking', icon: Users },
  { to: '/projects', label: 'Proyectos', icon: Briefcase },
  { to: '/events', label: 'Eventos', icon: Calendar },
  { to: '/meetings', label: 'Reuniones', icon: Video },
  { to: '/contact', label: 'Contacto', icon: Mail },
];

function HomeNavBar() {
  const profileImage = localStorage.getItem('profileImage');
  // `name` cubre las sesiones abiertas antes de guardar profileName
  const profileName = localStorage.getItem('profileName') || localStorage.getItem('name') || '';
  const profileSurname = localStorage.getItem('profileSurname') || '';
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  // Menú hamburguesa controlado: con los Link del router la página no se
  // recarga, así que sin esto el menú seguía abierto tras elegir una sección
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  // La barra se compacta y se opaca al bajar: menos ruido mientras se lee.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    clearSession();
    window.location.href = '/';
  };

  const isActive = (path) =>
    path === '/home'
      ? location.pathname === '/home' || location.pathname === '/'
      : location.pathname.startsWith(path);

  return (
    <Navbar
      expand="lg"
      variant="dark"
      sticky="top"
      expanded={expanded}
      onToggle={setExpanded}
      className={`dyd-nav ${scrolled ? 'is-scrolled' : ''}`}
    >
      <Container>
        <Navbar.Brand as={Link} to="/home" className="dyd-brand">
          <img src={logo} alt="DrivingYourDream Club" />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="dyd-nav" className="dyd-toggler">
          <span /><span /><span />
        </Navbar.Toggle>

        <Navbar.Collapse id="dyd-nav">
          <Nav className="mx-auto dyd-nav-links">
            {LINKS.map(({ to, label, icon: Icon }) => (
              <Nav.Link
                key={to}
                as={Link}
                to={to}
                onClick={() => setExpanded(false)}
                className={`dyd-nav-link ${isActive(to) ? 'active' : ''}`}
              >
                <Icon size={16} strokeWidth={2.2} />
                <span>{label}</span>
              </Nav.Link>
            ))}
          </Nav>

          <Nav className="dyd-nav-profile">
            <NavDropdown
              align="end"
              id="profile-dropdown"
              className="dyd-profile-dropdown"
              title={
                <Avatar
                  src={profileImage}
                  name={profileName}
                  surname={profileSurname}
                  size={38}
                  ring
                />
              }
            >
              {isAdmin && (
                <>
                  <NavDropdown.Item as={Link} to="/admin" onClick={() => setExpanded(false)} className="dyd-drop-item">
                    <Shield size={16} /> Administrar el sitio
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                </>
              )}
              <NavDropdown.Item as={Link} to="/profile" onClick={() => setExpanded(false)} className="dyd-drop-item">
                <User size={16} /> Ver perfil
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout} className="dyd-drop-item dyd-drop-danger">
                <LogOut size={16} /> Cerrar sesión
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>

      <style>{`
        .dyd-nav {
          padding: 0.85rem 0;
          background: rgba(8, 11, 17, 0.62);
          backdrop-filter: blur(18px) saturate(160%);
          -webkit-backdrop-filter: blur(18px) saturate(160%);
          border-bottom: 1px solid rgba(255,255,255,0.07);
          transition: padding 0.4s var(--dyd-ease), background 0.4s var(--dyd-ease),
                      border-color 0.4s var(--dyd-ease), box-shadow 0.4s var(--dyd-ease);
        }
        .dyd-nav.is-scrolled {
          padding: 0.5rem 0;
          background: rgba(7, 9, 13, 0.9);
          border-bottom-color: rgba(59,130,246,0.22);
          box-shadow: 0 10px 34px rgba(0,0,0,0.5);
        }
        /* Hilo de luz inferior */
        .dyd-nav::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: -1px;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(59,130,246,0.65), rgba(34,211,238,0.45), transparent);
          opacity: 0;
          transition: opacity 0.4s var(--dyd-ease);
        }
        .dyd-nav.is-scrolled::after { opacity: 1; }

        .dyd-brand { padding: 0; margin-right: 1.5rem; }
        .dyd-brand img {
          height: 46px;
          object-fit: contain;
          transition: height 0.4s var(--dyd-ease), filter 0.4s var(--dyd-ease);
          filter: drop-shadow(0 4px 14px rgba(59,130,246,0.35));
        }
        .dyd-nav.is-scrolled .dyd-brand img { height: 38px; }
        .dyd-brand:hover img { filter: drop-shadow(0 6px 20px rgba(59,130,246,0.65)); }

        .dyd-nav-links { gap: 2px; }

        .dyd-nav-link.nav-link {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 15px !important;
          border-radius: 999px;
          font-size: 0.88rem;
          font-weight: 500;
          color: var(--dyd-text-dim) !important;
          transition: color 0.3s var(--dyd-ease), background 0.3s var(--dyd-ease);
        }
        .dyd-nav-link svg { opacity: 0.65; transition: opacity 0.3s var(--dyd-ease), transform 0.3s var(--dyd-ease); }
        .dyd-nav-link:hover {
          color: #fff !important;
          background: rgba(255,255,255,0.06);
        }
        .dyd-nav-link:hover svg { opacity: 1; transform: translateY(-1px); }

        .dyd-nav-link.active {
          color: #fff !important;
          background: rgba(59,130,246,0.14);
          box-shadow: inset 0 0 0 1px rgba(59,130,246,0.32);
        }
        .dyd-nav-link.active svg { opacity: 1; color: var(--dyd-blue-light); }
        /* Punto indicador que aparece bajo la sección activa */
        .dyd-nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          width: 16px;
          height: 2px;
          border-radius: 2px;
          background: var(--dyd-accent-grad);
          transform: translateX(-50%);
          animation: dyd-rise 0.4s var(--dyd-ease);
        }

        .dyd-profile-dropdown .dropdown-toggle { padding: 0 !important; display: flex; align-items: center; }
        .dyd-profile-dropdown .dropdown-toggle::after { display: none; }
        .dyd-profile-dropdown .dropdown-toggle:hover .dyd-avatar-ring { transform: scale(1.06); }
        .dyd-profile-dropdown .dropdown-menu { min-width: 232px; margin-top: 12px; animation: dyd-pop 0.22s var(--dyd-ease); }

        .dyd-drop-item { display: flex !important; align-items: center; gap: 11px; }
        .dyd-drop-item svg { opacity: 0.7; }
        .dyd-drop-item:hover svg { opacity: 1; }
        .dyd-drop-danger { color: #fca5a5 !important; }
        .dyd-drop-danger:hover { background: rgba(248,113,113,0.16) !important; color: #fff !important; }

        /* Hamburguesa animada */
        .dyd-toggler.navbar-toggler {
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 11px;
          padding: 9px 10px;
          flex-direction: column;
          gap: 4px;
        }
        .dyd-toggler span {
          display: block;
          width: 20px;
          height: 2px;
          border-radius: 2px;
          background: #fff;
          transition: transform 0.35s var(--dyd-ease), opacity 0.35s var(--dyd-ease);
        }
        .dyd-toggler[aria-expanded="true"] span:nth-child(1) { transform: translateY(6px) rotate(45deg); }
        .dyd-toggler[aria-expanded="true"] span:nth-child(2) { opacity: 0; }
        .dyd-toggler[aria-expanded="true"] span:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }
        .dyd-toggler:focus { box-shadow: 0 0 0 3px rgba(59,130,246,0.25); }

        @media (max-width: 991px) {
          .dyd-nav { padding: 0.6rem 0; }
          .dyd-toggler.navbar-toggler { display: flex; }
          .navbar-collapse {
            margin-top: 1rem;
            padding: 14px;
            border-radius: var(--dyd-r-lg);
            background: rgba(13, 17, 25, 0.96);
            border: 1px solid rgba(255,255,255,0.1);
            box-shadow: var(--dyd-shadow-md);
            /* Con el menu abierto en horizontal no cabe entero: que haga
               scroll el panel y no se quede ningun enlace fuera */
            max-height: calc(100dvh - 120px);
            overflow-y: auto;
            overscroll-behavior: contain;
          }
          .dyd-nav-links { gap: 4px; }
          .dyd-nav-link.nav-link { justify-content: flex-start; padding: 12px 16px !important; }
          .dyd-nav-link.active::after { display: none; }
          .dyd-nav-profile { margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.08); }
          /* El desplegable del avatar dentro del menu: ancla a la izquierda y
             sin flotar, que si no se sale por el borde de la pantalla */
          .dyd-profile-dropdown .dropdown-menu {
            position: static !important;
            float: none;
            width: 100%;
            min-width: 0;
            margin-top: 10px;
            transform: none !important;
          }
          .dyd-profile-dropdown .dropdown-toggle { padding: 4px 0 !important; }
        }

        @media (max-width: 420px) {
          .dyd-brand { margin-right: 0.75rem; }
          .dyd-brand img { height: 38px; }
          .dyd-nav.is-scrolled .dyd-brand img { height: 33px; }
        }
      `}</style>
    </Navbar>
  );
}

export default HomeNavBar;
